import type { Shift } from "../types/Shift";
import { recentPastShifts } from "./rollover";
import { upcomingShifts } from "./upcomingShifts";

/**
 * The shifts whose jobs the scheduling board loads: the recent-ended window (MC parity: a past
 * shift stays listed while it carries unsampled jobs, so their sampled-ness must be known up
 * front), most-recent-first, then the upcoming shifts. The loaded shift list spans ~4 months of
 * history, hence the bounded windows.
 */
export function schedulingWindowShifts(shifts: Shift[], now: Date): Shift[] {
	return [...recentPastShifts(shifts, now), ...upcomingShifts(shifts, now)];
}

/**
 * The ids of the jobs on the scheduling window's shifts, de-duplicated and sorted — so two shift
 * lists with the same window membership give equal lists, whatever else about the shifts changed.
 *
 * Membership is the shifts' own `jobIds` — the forward link the portal owns (see
 * `shiftIdByJobId`) — never a job's `shiftId`.
 */
export function schedulingWindowJobIds(shifts: Shift[], now: Date): string[] {
	const ids = new Set<string>();
	for (const shift of schedulingWindowShifts(shifts, now)) for (const jobId of shift.jobIds) ids.add(jobId);
	return [...ids].sort();
}

/**
 * The jobs whose id is one of `jobIds`, in their input order.
 *
 * The scheduling board reads every scheduled, unsampled job in one request and narrows it here to
 * `schedulingWindowJobIds`: a job on a shift outside the window must not reach the map (scheduled
 * jobs are drawn, coloured, summed and auto-fitted wholesale), and a job that has left every
 * window shift drops out the moment the shift list says so, without the jobs being read again.
 */
export function jobsWithIds<J extends { id: string }>(jobs: readonly J[], jobIds: Iterable<string>): J[] {
	const wanted = new Set(jobIds);
	return jobs.filter((job) => wanted.has(job.id));
}
